/* eslint-disable max-lines */
import React from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { cx } from '@emotion/css';
import { css, keyframes } from '@emotion/react';
// ---- external imports ----

import { withFramerMotionPropsRenamed } from './hoc';
import { DeniedCursor, PointerCursor } from 'Cursors';
// ---- local imports ----


const assetFadeIn = keyframes`
	from {
		opacity: 0;
	}
	
	to {
		opacity: 1;
	}
`;

/* eslint-disable max-len */
const assetStyles = {
	absPosition: ({ xPos, yPos, shouldFadeIn = true }) => (css`
		user-select: none;
		position: absolute;
		${xPos ? `left: calc( var(--offsetLeft) + calc( var(--screenScale) * ${xPos}px ))` : ''};
		${yPos ? `top: calc( var(--offsetTop) + calc( var(--screenScale) * ${yPos}px ))` : ''};
		animation: ${assetFadeIn} ${shouldFadeIn ? "0.2": "0"}s ease-in-out;
	`),
	widthHeight: ({ projection, sizeScale }) => (css`
		${projection?.width ? `width: calc( var(--screenScale) * ${(projection?.width * sizeScale)}px )` : ''};
		${projection?.height ? `height: calc( var(--screenScale) * ${(projection?.height * sizeScale)}px )` : ''};
	`),
	font: ({ fontSize, lineHeight, letterSpacing }) => (css`
		${fontSize ? `font-size: calc( var(--screenScale) * ${fontSize}px)` : ''};
		${lineHeight ? `line-height: calc( var(--screenScale) * ${lineHeight}px )` : ''};
		${letterSpacing ? `letter-spacing: calc( var(--screenScale) * ${letterSpacing}px )` : ''};
	`),
	backgroundImg: (size) => ({ projection, disabled, dontDimWhenDisabled }) => (projection
		? css`
			background-size: ${size ?? 'contain'};
			background-repeat: no-repeat;
			background-color: transparent;
			background-image: url(${projection.src});
			${disabled && projection.src && !dontDimWhenDisabled ? 'filter: brightness(0.5)' : ''};
		`
		: null),
	backgroundHoverImg: (size) => ({ imgBgHover }) => (imgBgHover
		? css`
			background-size: ${size ?? 'contain'};
			background-repeat: no-repeat;
			background-color: transparent;
			background-image: url(${imgBgHover});
		`
		: null)
};
/* eslint-enable max-len */

function Asset() { }

Asset.Img = React.memo(withFramerMotionPropsRenamed(styled(motion.div)`
	user-select: none;
	${assetStyles.font};
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.backgroundImg('contain')};
`));

/**
 * @typedef {Object} AssetLinkProps
 * @property {number} y - y position
 * @property {Projection} projection - background image
 * @property {projection} [projectionHover] - background image on hover
 * @property {MapPropsFunc} [mapProps] - map `withFramerMotionPropsRenamed` props before passed to underlying component
 */

/**
 * @type {function(AssetLinkProps): JSX.Element}
 */
Asset.Link = React.memo(withFramerMotionPropsRenamed(styled(Link, {
	shouldForwardProp: isPropValid,
})`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};
	${assetStyles.backgroundImg('contain')};

	${PointerCursor};

	&:hover {
		${assetStyles.backgroundHoverImg('contain')};
	}
`));

/**
 * @typedef {Object} AssetDivProps
 * @property {number} x - x position
 * @property {number} y - y position
 * @property {Projection} projection - background image
 * @property {projection} [projectionHover] - background image on hover
 * @property {MapPropsFunc} [mapProps] - map `withFramerMotionPropsRenamed` props before passed to underlying component
 */

/**
 * @type {function(AssetDivProps): JSX.Element}
 */
Asset.Backdrop = React.memo(withFramerMotionPropsRenamed(styled(motion.div)`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};
	${assetStyles.backgroundImg('contain')};
	
	user-select: none;
	overflow: hidden;
	outline: none;
	
	font-family: "Nanum Pen Script", sans-serif;
	
	&:hover {
		${assetStyles.backgroundHoverImg('contain')};
	}
	
	& > * {
		${assetStyles.font};
		user-select: text;
	}
`));


/**
 * @typedef {Object} AssetTextProps
 * @property {number} x - x position
 * @property {number} y - y position
 * @property {Projection} projection - background image
 * @property {projection} [projectionHover] - background image on hover
 * @property {MapPropsFunc} [mapProps] - map `withFramerMotionPropsRenamed` props before passed to underlying component
 */

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.Text = React.memo(withFramerMotionPropsRenamed(styled(motion.span)`
	${assetStyles.absPosition};	
	${assetStyles.widthHeight};
	${assetStyles.font};
	
	user-select: none;
	
	font-family: "Nanum Pen Script", sans-serif;
`));

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.TextContainer = React.memo(withFramerMotionPropsRenamed(styled(motion.span)`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};

	user-select: none;
	overflow: hidden;
	
	& > * {
		${assetStyles.font};
		font-family: "Nanum Pen Script", sans-serif;
	}
`));

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.Input = React.memo(withFramerMotionPropsRenamed(styled(motion.input)`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};
	${assetStyles.backgroundImg('contain')};

	background-color: transparent;
	appearance: none;
	outline: none;
	border: none;
	padding: 0;
	margin: 0;
	color: #fff;
`));

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.TextArea = React.memo(withFramerMotionPropsRenamed(styled(motion.textarea)`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};
	${assetStyles.backgroundImg('contain')};

	background-color: transparent;
	appearance: none;
	outline: none;
	border: none;
	padding: 0;
	margin: 0;
	color: #fff;
	resize: none;
	overflow: hidden;
`));

/**
 * @typedef {Object} AssetTextImgProps
 * @property {number} x - x position
 * @property {number} y - y position
 * @property {string} className - wrapper element css class
 * @property {string} text - text value
 * @property {string} textClassName - text element css class
 * @property {string} imgClassName - img element css class
 * @property {string|function} textTag - html/React Component for text element
 * @property {Projection} projection - background image
 * @property {projection} [projectionHover] - background image on hover
 * @property {MapPropsFunc} [mapProps] - map `withFramerMotionPropsRenamed` props before passed to underlying component
 */

/**
 * @type {function(AssetTextImgProps): JSX.Element}
 */
Asset.TextImg = styled((props) => {
	const {
		text,
		textTag,
		className,
		imgClassName,
		textClassName,
		...propsRest
	} = props;

	const cn = cx('game-assets__text-img__wrapper', className);

	return (
		<motion.div
			className={cn}
		>
			<Asset.Img
				{...propsRest}
				className={imgClassName}
			/>
			<Asset.Text
				{...propsRest}
				as={textTag}
				className={textClassName}
			>
				{text}
			</Asset.Text>
		</motion.div>
	);
})`
	${assetStyles.absPosition};

	user-select: none;

	span {
		text-align: center;
		font-family: "Nanum Pen Script", sans-serif;
	}
`;
/**
 * @type {function(AssetLinkProps): JSX.Element}
 */
Asset.Button = React.memo(withFramerMotionPropsRenamed(styled(motion.button)`
	${assetStyles.absPosition};
	${assetStyles.widthHeight};
	${assetStyles.font};
	
	${assetStyles.backgroundImg('contain')};
	outline: none;

	${({ disabled }) => (disabled ? DeniedCursor : PointerCursor)}  ;
	
	${({ disabled, imgBgHover }) => {
		if (disabled || !imgBgHover) return '';

		return `
			&:hover {
				background-image: url(${imgBgHover});
			}
		`;
	}}
`));

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.Container = React.memo(withFramerMotionPropsRenamed(styled(motion.span)`
	${assetStyles.absPosition};
	${assetStyles.font};
	${assetStyles.widthHeight};
	
	user-select: none;
	
	& * {
		${assetStyles.font};
	}
`));

/**
 * @type {function(AssetTextProps): JSX.Element}
 */
Asset.DivContainer = React.memo(withFramerMotionPropsRenamed(styled(motion.div)`
	user-select: none;
	
	${assetStyles.absPosition};	
	${assetStyles.widthHeight};
	${assetStyles.font};
`));


Asset.Svg = React.memo(withFramerMotionPropsRenamed(styled(motion.svg)`
	${assetStyles.absPosition};	
	${assetStyles.widthHeight};
	${assetStyles.font};
`));



const withPropsForComp = (Comp, props) => {
	const isFunc = typeof props === 'function';

	const mergeProps = (ownProps) => ({
		...props,
		...ownProps,
	});

	const extendWrapperProps = isFunc
		? props
		: mergeProps;

	return React.forwardRef((ownProps, ref) => {
		return (<Comp
			className={ownProps.className} {...extendWrapperProps(ownProps)} ref={ref} />);
	});
};

const withPropsFnCreator = (Comp) => (props) => (withPropsForComp(Comp, props));
const withProjectionFnCreator =
	(Comp) => (projection, props) => (withPropsForComp(Comp, { ...props, projection }));

Asset.Button.withProps = withPropsFnCreator(Asset.Button);
Asset.Link.withProps = withPropsFnCreator(Asset.Link);
Asset.Backdrop.withProps = withPropsFnCreator(Asset.Backdrop);
Asset.Input.withProps = withPropsFnCreator(Asset.Input);
Asset.TextArea.withProps = withPropsFnCreator(Asset.TextArea);
Asset.Text.withProps = withPropsFnCreator(Asset.Text);
Asset.TextContainer.withProps = withPropsFnCreator(Asset.TextContainer);
Asset.DivContainer.withProps = withPropsFnCreator(Asset.DivContainer);
Asset.Img.withProps = withPropsFnCreator(Asset.Img);
Asset.Img.withProjection = withProjectionFnCreator(Asset.Img);

Asset.Button.withProjection = withProjectionFnCreator(Asset.Button);

export default Asset;
